import * as React from 'react'
import { Circle, CircleCheck, LoaderCircle } from 'lucide-react'
import { cn } from '../../lib/utils'

export function TaskCompletionCircle({
  title,
  done,
  saving,
  onClick,
  className
}: {
  title: string
  done: boolean
  saving: boolean
  onClick: () => void
  className?: string
}): React.ReactElement {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={done}
      aria-label={`${done ? 'Reopen' : 'Complete'} ${title}`}
      disabled={saving}
      onClick={onClick}
      className={cn(
        'z-10 -ml-0.5 inline-flex size-6 items-center justify-center rounded-full outline-none transition-colors hover:bg-st-run-bg focus-visible:ring-2 focus-visible:ring-brand disabled:pointer-events-none disabled:opacity-50',
        done
          ? 'text-st-run dark:text-emerald-300'
          : 'text-ink-4 hover:text-st-run dark:hover:text-emerald-300',
        className
      )}
    >
      {done ? (
        <CircleCheck size={15} aria-hidden="true" />
      ) : (
        <Circle size={15} aria-hidden="true" />
      )}
    </button>
  )
}

export function TaskBulkAction({
  problemName,
  allDone,
  saving,
  onClick
}: {
  problemName: string
  allDone: boolean
  saving: boolean
  onClick: () => void
}): React.ReactElement {
  const action = allDone ? 'Restore' : 'Complete'
  return (
    <div className="flex items-center border-b border-line/60 bg-bg-sunk/15 px-3 py-1.5">
      <button
        type="button"
        aria-label={`${action} all tasks for ${problemName}`}
        aria-busy={saving}
        disabled={saving}
        onClick={onClick}
        className={cn(
          '-ml-0.5 inline-flex h-6 items-center gap-1.5 rounded pr-2 text-12 leading-4 font-medium outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-brand disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none',
          allDone
            ? 'text-brand hover:bg-brand/5 dark:text-sky-300 dark:hover:bg-sky-400/10'
            : 'text-ink-3 hover:bg-st-run-bg/50 hover:text-st-run dark:hover:text-emerald-300'
        )}
      >
        <span className="inline-flex size-6 items-center justify-center" aria-hidden="true">
          {saving ? (
            <LoaderCircle size={15} className="animate-spin motion-reduce:animate-none" />
          ) : allDone ? (
            <CircleCheck size={15} />
          ) : (
            <Circle size={15} />
          )}
        </span>
        <span>{action} all</span>
      </button>
    </div>
  )
}
